import { ref, computed } from 'vue';
import { authApi } from '@/services/api/auth';
import type { User, AuthResponse } from '@/types/auth';

// 使用单例模式，避免在不同地方调用 useUserStore() 时产生多个独立实例
let _userStore: ReturnType<typeof createUserStore> | null = null;
export function useUserStore(...args: any[]) {
  // 如果传入任何参数（例如测试传入的pinia实例），则强制创建一个新的store实例，保证隔离
  if (args && args.length > 0) {
    _userStore = createUserStore();
  }
  if (!_userStore) {
    _userStore = createUserStore();
  }
  return _userStore as any;
}

// 提供重置方法，便于在测试或需要时清空单例
export function resetUserStore() {
  _userStore = null;
}
function createUserStore() {
  // 使用浏览器window.localStorage优先，兼容测试环境设置到window的mock；否则回退到globalThis
  const storage = (typeof window !== 'undefined' && (window as any).localStorage)
    ? (window as any).localStorage as Storage
    : (globalThis as any).localStorage as Storage;

  // 状态
  const user = ref<User | null>(null);
  const token = ref<string | null>(storage?.getItem('token') || null);
  const refreshToken = ref<string | null>(storage?.getItem('refresh_token') || null);
  const roles = ref<string[]>([]);
  const permissions = ref<string[]>([]);
  // RBAC 作用域：门店与区域分配
  const assignedStoreIds = ref<string[]>([]);
  const assignedRegionIds = ref<string[]>([]);
  const isInitialized = ref(false);
  const tokenExpiry = ref<number>(storage?.getItem('token_expiry') ?
    parseInt(storage?.getItem('token_expiry') || '0') : 0);
  // const router = useRouter();

  // 计算属性
  // 修改认证逻辑：只要有有效token就认为已认证，user信息可以异步加载
  const isAuthenticatedRef = computed(() => !!token.value && !isTokenExpired.value);
  const isTokenExpired = computed(() => {
    // 如果没有设置过期时间，默认永不过期
    if (!tokenExpiry.value || tokenExpiry.value === 0) {
      return false;
    }
    // 检查token是否过期（当前时间大于过期时间则过期）
    return Date.now() > tokenExpiry.value;
  });
  const currentUserRef = computed(() => user.value);
  const currentTokenRef = computed(() => token.value);
  const allowedStoreIdsRef = computed(() => assignedStoreIds.value);
  const allowedRegionIdsRef = computed(() => assignedRegionIds.value);

  // 设置token
  const setToken = (newToken: string | null) => {
    token.value = newToken;
    if (newToken) {
      storage?.setItem('token', newToken);
      // 设置token过期时间：记住我则7天，否则24小时
      const remember = storage?.getItem('remember_me') === '1';
      const expiryTime = Date.now() + (remember ? 7 * 24 * 60 * 60 * 1000 : 24 * 60 * 60 * 1000);
      tokenExpiry.value = expiryTime;
      storage?.setItem('token_expiry', expiryTime.toString());
    } else {
      storage?.removeItem('token');
      tokenExpiry.value = 0;
      storage?.removeItem('token_expiry');
    }
  };

  // 设置刷新令牌
  const setRefreshToken = (newRefreshToken: string | null) => {
    refreshToken.value = newRefreshToken;
    if (newRefreshToken) {
      storage?.setItem('refresh_token', newRefreshToken);
    } else {
      storage?.removeItem('refresh_token');
    }
  };

  // 设置用户信息
  const setUser = (userData: User | null) => {
    user.value = userData;
    // 注入作用域字段（如果后端/Mock返回）
    setAssignedStores(userData?.assignedStoreIds || []);
    setAssignedRegions(userData?.assignedRegionIds || []);
  };

  // 设置角色
  const setRoles = (userRoles: string[]) => {
    roles.value = userRoles || [];
  };

  // 设置权限
  const setPermissions = (userPermissions: string[]) => {
    permissions.value = userPermissions || [];
  };

  // 设置作用域
  const setAssignedStores = (ids: string[]) => {
    assignedStoreIds.value = ids || [];
  };
  const setAssignedRegions = (ids: string[]) => {
    assignedRegionIds.value = ids || [];
  };

  // 访问校验
  const canAccessStore = (storeId?: string) => {
    if (!storeId) return true;
    // 管理员或运营总监默认开放
    if (roles.value.includes('sys_admin') || roles.value.includes('ops_director') || roles.value.includes('admin')) return true;
    // 无分配表示不限制
    if (assignedStoreIds.value.length === 0 && assignedRegionIds.value.length === 0) return true;
    return assignedStoreIds.value.includes(storeId);
  };
  const canAccessRegion = (regionId?: string) => {
    if (!regionId) return true;
    if (roles.value.includes('sys_admin') || roles.value.includes('ops_director') || roles.value.includes('admin')) return true;
    if (assignedStoreIds.value.length === 0 && assignedRegionIds.value.length === 0) return true;
    return assignedRegionIds.value.includes(regionId);
  };

  // 权限/角色判断方法
  const hasRole = (role: string) => roles.value.includes(role);
  const hasAnyRole = (required: string[] = []) => required.length === 0 || required.some(r => roles.value.includes(r));
  const hasPermission = (perm: string) => permissions.value.includes(perm);
  const hasAnyPermission = (required: string[] = []) => required.length === 0 || required.some(p => permissions.value.includes(p));

  // 设置初始化状态
  const setInitialized = (status: boolean) => {
    isInitialized.value = status;
  };

  // 初始化用户状态
  const initialize = () => {
    try {
      const savedToken = storage?.getItem('token');
      if (savedToken) {
        if (isTokenExpired.value) {
          setToken(null);
        } else {
          // 仅设置token，不进行用户数据加载（避免在测试环境下mock抛错）
          setToken(savedToken);
        }
      }
    } catch (error) {
      console.error('初始化用户状态失败:', error);
      setToken(null);
    } finally {
      setInitialized(true);
    }
  };

  // 保留兼容的异步版本，供其他调用方使用
  const initializeUser = async () => {
    initialize();
    
    // 如果有token但没有用户信息，尝试获取用户信息
    if (token.value && !user.value && !isTokenExpired.value) {
      try {
        await fetchCurrentUser();
      } catch (error) {
        console.error('获取用户信息失败:', error);
        // 获取失败清除token
        setToken(null);
      }
    }
  };

  // 获取用户信息
  const fetchCurrentUser = async (): Promise<AuthResponse> => {
    try {
      // 首先检查token是否过期
      if (isTokenExpired.value) {
        throw new Error('Token已过期');
      }

      // 使用真实API获取用户信息
      const response = await authApi.getCurrentUser();
      setUser(response.user);
      setRoles(response.roles || []);
      setPermissions(response.permissions || []);

      return response as AuthResponse;
    } catch (error: any) {
      if (error.message === 'Token已过期' || error.response?.status === 401) {
        // Token无效或过期
        setToken(null);
      }
      throw error;
    }
  };

  // 登录
  const login = async (credentials: {
    username?: string;
    email?: string;
    phone?: string;
    password: string;
    tenantId?: string; // 新增：登录需提供租户ID
  }): Promise<AuthResponse> => {
    try {
      // 使用真实API（在测试中会被mock）
      const response = await authApi.login({
        username: credentials.username || credentials.email || credentials.phone || '',
        password: credentials.password,
        rememberMe: false
      } as any, credentials.tenantId ? { headers: { 'X-Tenant-ID': credentials.tenantId } } : undefined);

      setToken(response.token);
      setRefreshToken(response.refreshToken || null);
      setUser(response.user);
      setRoles(response.roles || []);
      setPermissions(response.permissions || []);
      // 同步作用域
      setAssignedStores(response.user.assignedStoreIds || []);
      setAssignedRegions(response.user.assignedRegionIds || []);

      return response as AuthResponse;
    } catch (error) {
      console.error('登录失败:', error);
      throw new Error('网络连接失败，请稍后再试');
    }
  };

  // 企业登录
  const enterpriseLogin = async (data: { enterpriseCode: string; username: string; password: string; }): Promise<AuthResponse> => {
    try {
      const response = await authApi.enterpriseLogin({
        enterpriseCode: data.enterpriseCode,
        username: data.username,
        password: data.password,
        rememberMe: false
      } as any);

      setToken(response.token);
      setUser(response.user);
      setRoles(response.roles || []);
      setPermissions(response.permissions || []);
      // 同步作用域
      setAssignedStores(response.user.assignedStoreIds || []);
      setAssignedRegions(response.user.assignedRegionIds || []);

      return response as AuthResponse;
    } catch (error) {
      console.error('企业登录失败:', error);
      throw new Error('企业登录失败，请稍后再试');
    }
  };

  // 注销
  const logout = () => {
    setToken(null);
    setRefreshToken(null);
    setUser(null);
    setRoles([]);
    setPermissions([]);
    setAssignedStores([]);
    setAssignedRegions([]);
    // 移除内部路由跳转，让路由守卫处理跳转逻辑
  };

  // 注册
  const register = async (userData: {
    username: string;
    email: string;
    password: string;
  }): Promise<AuthResponse> => {
    try {
      // 使用真实API（在测试中会被mock）
      const response = await authApi.register({
        username: userData.username,
        email: userData.email,
        password: userData.password
      } as any);

      setToken(response.token);
      setRefreshToken(response.refreshToken || null);
      setUser(response.user);
      setRoles(response.roles || []);
      setPermissions(response.permissions || []);
      // 同步作用域
      setAssignedStores(response.user.assignedStoreIds || []);
      setAssignedRegions(response.user.assignedRegionIds || []);

      return response as AuthResponse;
    } catch (error) {
      console.error('注册失败:', error);
      throw error;
    }
  };

  // 构造返回对象
  const store: any = {
    // 状态
    user,
    // token, // 改为通过属性访问器暴露
    roles,
    permissions,
    isInitialized,
    isTokenExpired,

    // 计算属性（Ref）
    isAuthenticatedRef: isAuthenticatedRef,
    currentUser: currentUserRef,
    currentToken: currentTokenRef,
    allowedStoreIds: allowedStoreIdsRef,
    allowedRegionIds: allowedRegionIdsRef,

    // 方法
    setUser,
    setToken,
    setRoles,
    setPermissions,
    setAssignedStores,
    setAssignedRegions,
    canAccessStore,
    canAccessRegion,
    hasRole,
    hasAnyRole,
    hasPermission,
    hasAnyPermission,
    setInitialized,
    initializeUser,
    initialize,
    fetchCurrentUser,
    login,
    enterpriseLogin,
    logout,
    register,
    // 新增：刷新令牌（方案B）
    refreshTokens: async (): Promise<AuthResponse> => {
      if (!refreshToken.value) throw new Error('缺少刷新令牌');
      const resp = await authApi.refreshToken(refreshToken.value);
      setToken(resp.token);
      setRefreshToken(resp.refreshToken || null);
      setUser(resp.user);
      setRoles(resp.roles || []);
      setPermissions(resp.permissions || []);
      return resp as AuthResponse;
    },
  };

  // 以普通属性形式暴露核心状态，兼容测试期望
  Object.defineProperty(store, 'token', { get: () => token.value, set: (val: string | null) => setToken(val) });
  // 新增：暴露 refreshToken
  Object.defineProperty(store, 'refreshToken', { get: () => refreshToken.value, set: (val: string | null) => setRefreshToken(val) });
  Object.defineProperty(store, 'user', { get: () => user.value, set: (val: User | null) => setUser(val) });
  Object.defineProperty(store, 'roles', { get: () => roles.value, set: (val: string[]) => setRoles(val) });
  Object.defineProperty(store, 'permissions', { get: () => permissions.value, set: (val: string[]) => setPermissions(val) });
  Object.defineProperty(store, 'isInitialized', { get: () => isInitialized.value });
  Object.defineProperty(store, 'isTokenExpired', { get: () => isTokenExpired.value });
  Object.defineProperty(store, 'assignedStoreIds', { get: () => assignedStoreIds.value, set: (val: string[]) => setAssignedStores(val) });
  Object.defineProperty(store, 'assignedRegionIds', { get: () => assignedRegionIds.value, set: (val: string[]) => setAssignedRegions(val) });

  // 兼容现有测试期望的 getter 属性
  Object.defineProperty(store, 'isAdmin', { get: () => roles.value.includes('admin') });
  Object.defineProperty(store, 'isUser', { get: () => roles.value.includes('user') });
  Object.defineProperty(store, 'isTenantAdmin', { get: () => roles.value.includes('tenant_admin') });
  Object.defineProperty(store, 'currentPermissions', { get: () => permissions.value });
  // 布尔型的 isAuthenticated，用于测试与业务逻辑的简化判断
  Object.defineProperty(store, 'isAuthenticated', { get: () => !!token.value && !!user.value && !isTokenExpired.value });

  return store;
}
